'use client'

import Link from 'next/link'
import { ArrowLeft, Users, Coffee, Code, Heart } from 'lucide-react'
import { useI18n } from '@/lib/i18n'

export default function AboutPage() {
  const { t } = useI18n()
  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-12 max-w-3xl">
        {/* Back link */}
        <Link 
          href="/" 
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-8"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('about.backToSearch', 'Back to search')}
        </Link>

        {/* Header */}
        <h1 className="text-3xl font-bold text-foreground mb-2">{t('about.title', 'About Roostr')}</h1>
        <p className="text-lg text-muted-foreground mb-12">{t('about.subtitle', 'Fast, user-friendly, AI-powered apartment search.')}</p>

        {/* Content */}
        <div className="space-y-12">
          
          {/* Who we are */}
          <section>
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              {t('about.whoWeAre', 'Who We Are')}
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              {t('about.whoWeAreText', "We're two people. We work from our apartments — the ones we found the hard way, before Roostr existed.")}
            </p>
          </section>

          {/* Why we built this */}
          <section>
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Code className="h-5 w-5 text-primary" />
              {t('about.whyBuilt', 'Why We Built This')}
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              {t('about.whyBuiltText', 'We want a service that is fast, easy to use, and quick to navigate.')}
            </p>
          </section>

          {/* The tech */}
          <section>
            <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Coffee className="h-5 w-5 text-primary" />
              {t('about.nerdyBit', 'The Nerdy Bit')}
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              {t('about.nerdyIntro', 'This entire website was built with the help of')}{' '}
              <a
                href="https://cursor.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                Cursor IDE
              </a>
              {' '}{t('about.nerdyMiddle', 'and')}{' '}
              <a
                href="https://claude.com/claude-code"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                Claude Code
              </a>
              . {t('about.nerdyOutro', 'Yes, we pair-programmed with an AI.')}
            </p>
          </section>

          {/* Contact */}
          <section className="pt-8 border-t border-border">
            <p className="text-muted-foreground leading-relaxed flex items-center gap-2">
              <Heart className="h-4 w-4 text-primary fill-primary" />
              {t('about.contactText', 'Want to say hi? Drop us a line at')}{' '}
              <a href="mailto:hello@roostr.me" className="text-primary hover:underline">
                hello@roostr.me
              </a>
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              {t('about.contactNote', "(We read every email. There's only two of us, remember?)")}
            </p>
          </section>

        </div>
      </div>
    </div>
  )
}
